import { useState } from "react";
import { X, Envelope, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { saveProfile } from "../lib/follows";

/**
 * Smart follow-prompt modal. Two modes:
 *
 *   mode="email"  — user has no email on file. Capture email + opt-in
 *                   together (checkbox defaults on).
 *   mode="optin"  — user already has email but hasn't said yes to alerts
 *                   yet. One-tap Yes / No buttons.
 *
 * All copy is intentionally short. Mobile-first — modal is a bottom sheet
 * on phones and a centered card on desktop.
 */
export default function FollowPromptModal({ mode, truckName, onClose }) {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [loading, setLoading] = useState(false);

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await saveProfile({ email: trimmed, optIn });
      toast.success(optIn ? "You'll get a weekly heads-up 👀" : "Email saved");
      onClose?.();
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setLoading(false);
    }
  };

  const confirmOptIn = async (yes) => {
    setLoading(true);
    try {
      await saveProfile({ optIn: yes });
      toast.success(yes ? "You'll be notified when they're out 👀" : "No alerts — got it");
      onClose?.();
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="follow-prompt-modal"
    >
      <div
        className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-5 pb-7 sm:pb-5 relative animate-slide-up shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted"
          aria-label="Close"
          data-testid="follow-prompt-close"
        >
          <X size={18} />
        </button>

        {mode === "email" ? (
          <form onSubmit={submitEmail}>
            <div className="flex items-center gap-2 mb-2">
              <Envelope size={20} weight="duotone" className="text-primary" />
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Weekly heads-up
              </div>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-1">
              Want updates when these trucks are out?
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Drop your email and we'll send you a quick Sunday note listing where your
              followed trucks will be that week. No login, no spam — unsubscribe anytime.
            </p>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mb-3"
              data-testid="follow-prompt-email-input"
              autoFocus
            />
            <label className="flex items-center gap-2 text-sm mb-4 select-none">
              <input
                type="checkbox"
                checked={optIn}
                onChange={(e) => setOptIn(e.target.checked)}
                className="accent-primary w-4 h-4"
                data-testid="follow-prompt-optin-checkbox"
              />
              <span>Send me a weekly update (recommended)</span>
            </label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                data-testid="follow-prompt-skip"
              >
                Not now
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
                data-testid="follow-prompt-save"
              >
                {loading ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Bell size={20} weight="duotone" className="text-primary" />
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Notify me
              </div>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-1">
              Want to be notified when {truckName || "this truck"} is out?
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              You'll get a quick weekly heads-up on Sunday listing everywhere your followed
              trucks will be. Unsubscribe anytime.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => confirmOptIn(false)}
                disabled={loading}
                className="flex-1"
                data-testid="follow-prompt-no"
              >
                No thanks
              </Button>
              <Button
                type="button"
                onClick={() => confirmOptIn(true)}
                disabled={loading}
                className="flex-1"
                data-testid="follow-prompt-yes"
              >
                {loading ? "Saving…" : "Yes, notify me"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
